import './LandingWrap.scss'

import { useFn } from '@eturi/react'
import { useT } from '@eturi/react-i18next'
import type { CssGlyphName } from '@op/icons'
import { useResizeObserver } from '@op/react-web'
import { benefitGroup$ } from '@op/services'
import cls from 'classnames'
import { type ReactNode, useLayoutEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { affiliateInfo$ } from '../../compound-selectors/affiliate'
import { BOTTOM_SHEET_HANDLE_HT } from '../../ghetto-commons'
import { useIsScreenSize, usePolling } from '../../hooks'
import { setPagePaddingVisible } from '../../reducers/app-misc.slice'
import { useStyle } from '../../styles/createStyles'
import css from '../../styles/exports.module.scss'
import { clearLandingSSOIfExpired } from '../../thunks/landing'
import { useAppDispatch } from '../../types'
import { Glyph, LoadEmpty } from '../../widgets'
import type { BottomSheetInstance, SnapPoint } from '../../widgets/BottomSheet'
import { BottomSheet } from '../../widgets/BottomSheet/lazy'
import { Footer } from '../Footer'
import { SubFeatures } from '../Subscription'
import { LandingLogo } from './LandingLogo'

type AffiliateWrapProps = {
	readonly children?: ReactNode
	readonly noDrawer?: boolean
	readonly noFeatures?: boolean
}

export const LandingWrap = ({
	children,
	noDrawer = false,
	noFeatures = false,
}: AffiliateWrapProps) => {
	const [t] = useT()
	const d = useAppDispatch()
	const affiliateInfo = useSelector(affiliateInfo$)
	const benefitGroup = useSelector(benefitGroup$)
	const bottomSheetRef = useRef<BottomSheetInstance>(null)
	const isScreenLg = useIsScreenSize('lg')

	const [canScrollDrawer, setCanScrollDrawer] = useState(false)

	const groupBenefitDiscountHeader = benefitGroup?.discount_header
	const showFeatures = !noFeatures && isScreenLg
	const showFeatureDrawer = !(noFeatures || noDrawer || isScreenLg)
	const [formQuoteTopOffset, setFormQuoteTopOffset] = useState(0)

	const handleFeaturesClick = useFn(() => {
		bottomSheetRef.current?.snapToIdx(1)
	})

	/**
	 * Measures the height of the quote-containing box on desktop view, so we can better align the
	 * form on the right.
	 */
	const [lgQuoteCallbackRef] = useResizeObserver(({ height }) => {
		setFormQuoteTopOffset(height)
	})

	const handleDrawerIdxChange = useFn((idx: number) => setCanScrollDrawer(idx > 0))

	/**
	 * Add padding to the top of the right form view when there is an affiliate quote. This pushes
	 * the form down so it's roughly aligned with the features box on the left. It's not perfectly
	 * aligned due to the fact that the gradient box has some padding around it as well. However,
	 * visually, it looks consistent enough, and adding the additional gradient box padding would
	 * complicate things for little gain.
	 */
	const formRightStyle = useStyle(
		() => ({ paddingTop: formQuoteTopOffset || undefined }),
		[formQuoteTopOffset],
	)

	/**
	 * This provides the correct height and compensatory padding when the feature drawer is visible.
	 *
	 * Rather than add a `.landing-wrap--drawer` class, we set this particular style using JS. We
	 * want to use values that are understood between the JS and CSS implementations. Because we
	 * can only transfer values from SASS to JS and not the other way around, and because we are
	 * defining the bottom sheet values like `COLLAPSED_DRAWER_SIZE` and `BOTTOM_SHEET_HANDLE_HT`
	 * in JS already, it's easier to export the single `pageWrapPadding` variable and use it in JS,
	 * rather than defining all those arbitrary values in SCSS, and parsing and exporting them.
	 */
	const landingWrapWithDrawerStyle = useStyle(() => {
		let gridTemplateRows: string | undefined = undefined
		let paddingBottom: string | undefined = undefined

		if (showFeatureDrawer) {
			paddingBottom = `${COLLAPSED_DRAWER_SIZE + BOTTOM_SHEET_HANDLE_HT}px`
			gridTemplateRows = `minmax(calc(100vh - ${paddingBottom} - ${css.pageWrapPadding}), auto) auto`
		}

		return { gridTemplateRows, paddingBottom }
	}, [showFeatureDrawer])

	const landingWrapCls = cls('landing-wrap', {
		'landing-wrap--features': !noFeatures,
		'landing-wrap--no-drawer': noDrawer && !isScreenLg,
		'landing-wrap--simple-view': noFeatures,
	})

	// Poll regularly to clear SSO if it becomes expired
	usePolling([() => d(clearLandingSSOIfExpired())])

	/**
	 * Reset `canScrollDrawer` if the feature drawer is removed.
	 *
	 * If the drawer is opened (setting `canScrollDrawer=true`), and the window is resized to remove
	 * the drawer, `canScrollDrawer` will stay `true`. If we then change the screen size to again
	 * show the drawer, `canScrollDrawer` will be wrong, allowing overflow scrolling to occur when
	 * the drawer is collapsed. It's a minor thing but easy to fix.
	 */
	useLayoutEffect(() => {
		if (!showFeatureDrawer) setCanScrollDrawer(false)
	}, [showFeatureDrawer])

	useLayoutEffect(() => {
		d(setPagePaddingVisible(!showFeatures))

		return () => {
			d(setPagePaddingVisible(true))
		}
	}, [showFeatures])

	return (
		<section className={landingWrapCls} style={landingWrapWithDrawerStyle}>
			{!noFeatures && (
				<div className="landing-wrap__gradient">
					<div className="landing-wrap__gradient-content">
						{affiliateInfo?.quote && (
							<div ref={lgQuoteCallbackRef} className="px-6 pb-5">
								<p className="font-bold italic leading-snug mb-1 text-balance text-center text-white text-xl">
									&ldquo;{affiliateInfo.quote}&rdquo;
								</p>

								{affiliateInfo?.name && (
									<p className="font-bold text-white text-end">- {affiliateInfo.name}</p>
								)}
							</div>
						)}

						<div className="bg-white rounded-3xl p-6 sm:p-9">
							<AffiliateSubFeatures />
						</div>
					</div>
				</div>
			)}

			<div className="landing-wrap__right" style={formRightStyle}>
				<div className="landing-wrap__right-content">
					<LandingLogo className={cls(noFeatures && 'mt-2')} affiliateLogo={affiliateInfo?.logo} />

					{groupBenefitDiscountHeader && (
						<p className="text-center mb-2 -mt-4 font-bold">{groupBenefitDiscountHeader}</p>
					)}

					{!isScreenLg && !groupBenefitDiscountHeader && affiliateInfo?.quote && (
						<div className="flex flex-col mx-auto mb-3">
							<p className="italic leading-snug mb-1 text-balance text-center sm:text-lg sm:leading-snug">
								&ldquo;{affiliateInfo.quote}&rdquo;
							</p>

							{affiliateInfo?.name && <p className="text-sm text-end">- {affiliateInfo?.name}</p>}
						</div>
					)}

					<div className="mx-auto">{children}</div>
				</div>
			</div>

			<div className={cls(!showFeatures && 'hidden')} />
			<Footer />

			{showFeatureDrawer && (
				<LoadEmpty>
					<BottomSheet
						ref={bottomSheetRef}
						onChange={handleDrawerIdxChange}
						onHandleTap={handleFeaturesClick}
						showOverflow={!canScrollDrawer}
						snapPoints={FEATURE_DRAWER_SNAP_POINTS}
					>
						<div className="flex px-2 py-3 w-full z-hack md:px-0">
							<div className="flex-center flex-1">
								<div className="font-bold -mt-1 mx-1 text-md uppercase whitespace-nowrap sm:text-lg">
									{t('partner.affiliate.features')}
								</div>
							</div>
						</div>
						<div className="pb-6 px-4">
							<SubFeatures tier={'premium_plus'} />
						</div>
					</BottomSheet>
				</LoadEmpty>
			)}
		</section>
	)
}

const FeatureHeader = ({ title, color }: { readonly title: string; readonly color: string }) => (
	<h4 className="my-2">
		<p className={cls('font-bold uppercase whitespace-nowrap', color)}>{title}</p>
	</h4>
)

type FeatureColProps = {
	readonly color: string
	readonly glyph: CssGlyphName
	readonly subtitle: string
	readonly title: string
}

const FeatureCol = ({ color, glyph, subtitle, title }: FeatureColProps) => {
	return (
		<div className="flex mx-1 my-1 flex-[0.5] items-center">
			<Glyph name={glyph} className={cls('mr-3', color)} size="lg" />

			<div className="shrink">
				<p className="font-bold leading-6">{title}</p>
				<p className="text-sm leading-6">{subtitle}</p>
			</div>
		</div>
	)
}

const AffiliateSubFeatures = () => {
	const [t] = useT('subscription_features')

	return (
		<div className="flex-col">
			<div className="flex flex-row gap-6 xl:gap-12">
				<div className="flex-grow basis-0">
					<FeatureHeader title={t('screentime_management')} color="text-ft-screentime" />

					<FeatureCol
						color="text-ft-screentime"
						glyph="clock"
						subtitle={t('feature.unlimited_schedules.subtitle')}
						title={t('feature.unlimited_schedules.title')}
					/>

					<FeatureCol
						color="text-ft-screentime"
						glyph="cancel"
						subtitle={t('feature.unlimited_blocks.subtitle')}
						title={t('feature.unlimited_blocks.title')}
					/>

					<FeatureCol
						color="text-ft-screentime"
						glyph="allowance"
						subtitle={t('feature.allowance.subtitle')}
						title={t('feature.allowance.title')}
					/>

					<FeatureCol
						color="text-ft-screentime"
						glyph="app-rules"
						subtitle={t('feature.app_rules.subtitle_affiliate')}
						title={t('feature.app_rules.title')}
					/>

					<FeatureCol
						color="text-ft-screentime"
						glyph="web"
						subtitle={t('feature.web_restrictions.subtitle')}
						title={t('feature.web_restrictions.title')}
					/>

					<FeatureCol
						color="text-ft-screentime"
						glyph="chat"
						subtitle={t('feature.block_texting.subtitle')}
						title={t('feature.block_texting.title')}
					/>
				</div>

				<div className="flex-grow basis-0">
					<FeatureHeader title={t('monitor_activity')} color="text-ft-monitor" />

					<FeatureCol
						color="text-ft-monitor"
						glyph="vew-now"
						subtitle={t('feature.view_screenshots.subtitle')}
						title={t('feature.view_screenshots.title')}
					/>

					<FeatureCol
						color="text-ft-monitor"
						glyph="interval"
						subtitle={t('feature.set_frequency.subtitle')}
						title={t('feature.set_frequency.title')}
					/>

					<FeatureCol
						color="text-ft-monitor"
						glyph="gallery-img"
						subtitle={t('feature.encrypted_gallery.subtitle')}
						title={t('feature.encrypted_gallery.title')}
					/>

					<FeatureCol
						color="text-ft-monitor"
						glyph="gallery-search"
						subtitle={t('feature.filter_images.subtitle')}
						title={t('feature.filter_images.title')}
					/>

					<FeatureCol
						color="text-ft-monitor"
						glyph="lips"
						subtitle={t('feature.flag_content.subtitle')}
						title={t('feature.flag_content.title')}
					/>
				</div>
			</div>

			<div className="flex flex-row gap-6 xl:gap-12">
				<div className="flex-grow basis-0">
					<FeatureHeader title={t('family_locator')} color="text-ft-locator" />

					<FeatureCol
						color="text-ft-locator"
						glyph="map"
						subtitle={t('feature.location_history.prem_plus_range')}
						title={t('feature.location_history.title')}
					/>

					<FeatureCol
						color="text-ft-locator"
						glyph="places"
						subtitle={t('feature.custom_places.subtitle')}
						title={t('feature.custom_places.title')}
					/>

					<FeatureCol
						color="text-ft-locator"
						glyph="crosshair-circle"
						subtitle={t('feature.current_location.subtitle')}
						title={t('feature.current_location.title')}
					/>

					<FeatureCol
						color="text-ft-locator"
						glyph="directions"
						subtitle={t('feature.directions.subtitle')}
						title={t('feature.directions.title')}
					/>
				</div>

				<div className="flex-grow basis-0">
					<FeatureHeader title={t('supervision')} color="text-ft-supervision" />

					<FeatureCol
						color="text-ft-supervision"
						glyph="add-user"
						subtitle={t('feature.manage_devices.subtitle')}
						title={t('feature.manage_devices.title')}
					/>

					{/* <FeatureCol
						color="text-ft-supervision"
						glyph="lock"
						subtitle={t('feature.tamperproof.subtitle')}
						title={t('feature.tamperproof.title')}
					/> */}

					<FeatureCol
						color="text-ft-supervision"
						glyph="info-bubble"
						subtitle={t('feature.app_alerts.subtitle')}
						title={t('feature.app_alerts.title')}
					/>

					<FeatureCol
						color="text-ft-supervision"
						glyph="battery"
						subtitle={t('feature.battery_life.subtitle')}
						title={t('feature.battery_life.title')}
					/>
				</div>
			</div>
		</div>
	)
}

const COLLAPSED_DRAWER_SIZE = 50
const FEATURE_DRAWER_SNAP_POINTS = [COLLAPSED_DRAWER_SIZE, '100%'] satisfies SnapPoint[]
